/* ===========================================
   COLOMBIA SHOP - THEME JS
   Mobile-First, Conversion-Optimized
   =========================================== */

'use strict';

// ─── ANNOUNCEMENT BAR ROTATOR ───
const AnnouncementBar = {
  messages: null,
  currentIndex: 0,
  interval: null,

  init() {
    this.messages = document.querySelectorAll('.announcement-message');
    if (this.messages.length <= 1) return;
    this.start();
  },

  start() {
    this.interval = setInterval(() => this.rotate(), 3500);
  },

  rotate() {
    this.messages[this.currentIndex].classList.remove('active');
    this.currentIndex = (this.currentIndex + 1) % this.messages.length;
    this.messages[this.currentIndex].classList.add('active');
  }
};

// ─── MOBILE MENU ───
const MobileMenu = {
  btn: null,
  menu: null,
  overlay: null,
  closeBtn: null,
  isOpen: false,

  init() {
    this.btn = document.getElementById('hamburger-btn');
    this.menu = document.getElementById('mobile-menu');
    this.overlay = document.getElementById('mobile-menu-overlay');
    this.closeBtn = document.getElementById('mobile-menu-close');

    if (!this.btn || !this.menu) return;

    this.btn.addEventListener('click', () => this.toggle());
    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    
    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });
    
    // Close when nav link clicked on mobile
    this.menu.querySelectorAll('.mobile-nav-link').forEach(link => {
      link.addEventListener('click', () => this.close());
    });
  },

  toggle() {
    this.isOpen ? this.close() : this.open();
  },

  open() {
    this.isOpen = true;
    this.menu.classList.add('open');
    this.overlay.classList.add('active');
    this.btn.classList.add('active');
    this.btn.setAttribute('aria-expanded', 'true');
    this.menu.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    // Focus first link
    const firstLink = this.menu.querySelector('.mobile-nav-link');
    firstLink?.focus();
  },

  close() {
    this.isOpen = false;
    this.menu.classList.remove('open');
    this.overlay.classList.remove('active');
    this.btn.classList.remove('active');
    this.btn.setAttribute('aria-expanded', 'false');
    this.menu.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.btn.focus();
  }
};

// ─── SEARCH TOGGLE ───
const SearchBar = {
  toggle: null,
  bar: null,
  input: null,
  closeBtn: null,

  init() {
    this.toggle = document.getElementById('search-toggle');
    this.bar = document.getElementById('search-bar');
    this.input = this.bar?.querySelector('.search-input');
    this.closeBtn = document.getElementById('search-close');

    if (!this.toggle || !this.bar) return;

    this.toggle.addEventListener('click', () => this.open());
    this.closeBtn?.addEventListener('click', () => this.close());
    
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.bar.classList.contains('active')) this.close();
    });
  },

  open() {
    this.bar.classList.add('active');
    this.input?.focus();
  },

  close() {
    this.bar.classList.remove('active');
    this.toggle.focus();
  }
};

// ─── FAQ ACCORDION ───
const FAQ = {
  init() {
    document.querySelectorAll('.faq-question').forEach(btn => {
      btn.addEventListener('click', () => {
        const item = btn.closest('.faq-item');
        const isOpen = item.classList.contains('open');
        
        // Close all others
        document.querySelectorAll('.faq-item.open').forEach(openItem => {
          if (openItem !== item) openItem.classList.remove('open');
        });
        
        item.classList.toggle('open', !isOpen);
        btn.setAttribute('aria-expanded', String(!isOpen));
      });
    });
  }
};

// ─── PRODUCT GALLERY ───
const ProductGallery = {
  mainImg: null,
  thumbs: null,

  init() {
    this.mainImg = document.querySelector('.product-gallery__main img');
    this.thumbs = document.querySelectorAll('.thumb-btn');

    if (!this.mainImg || !this.thumbs.length) return;

    this.thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const newSrc = thumb.getAttribute('data-src');
        const newAlt = thumb.getAttribute('data-alt') || '';
        
        if (newSrc) {
          this.mainImg.src = newSrc;
          this.mainImg.alt = newAlt;
          const zoomSrc = thumb.getAttribute('data-zoom-src');
          if (zoomSrc) this.mainImg.setAttribute('data-zoom-src', zoomSrc);
          this.thumbs.forEach(t => t.classList.remove('active'));
          thumb.classList.add('active');
        }
      });
    });
  }
};

// ─── IMAGE ZOOM (visor con zoom in/out, arrastre y pellizco) ───
// Uso: agrega el atributo data-zoomable a un contenedor (o a la <img>).
// Opcional: data-zoom-src="..." en la <img> con una versión de mayor resolución.
const ImageZoom = {
  MIN: 1,
  MAX: 5,
  STEP: 1.4,
  scale: 1,
  x: 0,
  y: 0,
  pointers: new Map(),
  pinchDist: 0,
  dragged: false,
  downTarget: null,
  lastFocus: null,
  modal: null,

  init() {
    this.build();
    this.initHover();

    document.querySelectorAll('[data-zoomable]').forEach(el => {
      if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
      el.setAttribute('role', 'button');
      el.setAttribute('aria-label', 'Ampliar imagen');
    });

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-zoomable]');
      if (trigger) {
        e.preventDefault();
        this.open(trigger);
      }
    });

    document.addEventListener('keydown', (e) => {
      if (this.isOpen()) {
        if (e.key === 'Escape') this.close();
        else if (e.key === '+' || e.key === '=') this.zoomBy(this.STEP);
        else if (e.key === '-' || e.key === '_') this.zoomBy(1 / this.STEP);
        else if (e.key === '0') this.reset();
        return;
      }
      const trigger = e.target.closest && e.target.closest('[data-zoomable]');
      if (trigger && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        this.open(trigger);
      }
    });
  },

  // Zoom al pasar el mouse: se acerca siguiendo el cursor y se aleja al salir.
  // Solo en dispositivos con mouse; en celulares/tablets el toque abre el visor.
  initHover() {
    if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const LEVEL = 2.2;

    document.querySelectorAll('[data-zoomable]').forEach(box => {
      const getImg = () => (box.tagName === 'IMG' ? box : box.querySelector('img'));

      box.addEventListener('mouseenter', () => {
        const img = getImg();
        if (!img) return;
        box.classList.add('is-hover-zoom');
        img.style.transform = 'scale(' + LEVEL + ')';

        // Cargar la versión de alta resolución para que el zoom se vea nítido
        const hi = img.getAttribute('data-zoom-src');
        if (hi && img.src !== hi) {
          const pre = new Image();
          pre.onload = () => { if (img.getAttribute('data-zoom-src') === hi) img.src = hi; };
          pre.src = hi;
        }
      });

      box.addEventListener('mousemove', (e) => {
        const img = getImg();
        if (!img) return;
        const r = box.getBoundingClientRect();
        const px = ((e.clientX - r.left) / r.width) * 100;
        const py = ((e.clientY - r.top) / r.height) * 100;
        img.style.transformOrigin = px + '% ' + py + '%';
      });

      box.addEventListener('mouseleave', () => {
        const img = getImg();
        box.classList.remove('is-hover-zoom');
        if (img) img.style.transform = '';
      });
    });
  },

  build() {
    const m = document.createElement('div');
    m.className = 'zoom-modal';
    m.hidden = true;
    m.setAttribute('role', 'dialog');
    m.setAttribute('aria-modal', 'true');
    m.setAttribute('aria-label', 'Imagen ampliada');
    m.innerHTML =
      '<div class="zoom-modal__stage"><img class="zoom-modal__img" alt="" draggable="false"></div>' +
      '<div class="zoom-modal__toolbar">' +
        '<button type="button" data-zoom="out" aria-label="Alejar">−</button>' +
        '<span class="zoom-modal__level" aria-live="polite">100%</span>' +
        '<button type="button" data-zoom="in" aria-label="Acercar">+</button>' +
        '<button type="button" data-zoom="reset" class="zoom-modal__reset">Restablecer</button>' +
      '</div>' +
      '<button type="button" class="zoom-modal__close" aria-label="Cerrar">×</button>' +
      '<p class="zoom-modal__hint">Usa + / − , la rueda del ratón o pellizca para hacer zoom</p>';
    document.body.appendChild(m);

    this.modal = m;
    this.stage = m.querySelector('.zoom-modal__stage');
    this.img = m.querySelector('.zoom-modal__img');
    this.level = m.querySelector('.zoom-modal__level');
    this.btnIn = m.querySelector('[data-zoom="in"]');
    this.btnOut = m.querySelector('[data-zoom="out"]');
    this.closeBtn = m.querySelector('.zoom-modal__close');

    this.btnIn.addEventListener('click', () => this.zoomBy(this.STEP));
    this.btnOut.addEventListener('click', () => this.zoomBy(1 / this.STEP));
    m.querySelector('[data-zoom="reset"]').addEventListener('click', () => this.reset());
    this.closeBtn.addEventListener('click', () => this.close());

    this.stage.addEventListener('wheel', (e) => {
      e.preventDefault();
      this.zoomBy(e.deltaY < 0 ? 1.15 : 1 / 1.15, e.clientX, e.clientY);
    }, { passive: false });

    this.stage.addEventListener('dblclick', (e) => {
      if (this.scale > 1) this.reset();
      else this.zoomTo(2.5, e.clientX, e.clientY);
    });

    this.stage.addEventListener('pointerdown', (e) => this.onDown(e));
    this.stage.addEventListener('pointermove', (e) => this.onMove(e));
    this.stage.addEventListener('pointerup', (e) => this.onUp(e));
    this.stage.addEventListener('pointercancel', (e) => this.onUp(e));

    // Clic fuera de la imagen cierra el visor
    this.stage.addEventListener('click', () => {
      if (this.downTarget === this.stage && !this.dragged) this.close();
    });
  },

  isOpen() {
    return this.modal && !this.modal.hidden;
  },

  open(trigger) {
    const img = trigger.tagName === 'IMG' ? trigger : trigger.querySelector('img');
    if (!img) return;
    const src = img.getAttribute('data-zoom-src') || trigger.getAttribute('data-zoom-src') || img.currentSrc || img.src;
    if (!src) return;

    this.lastFocus = document.activeElement;
    this.img.src = src;
    this.img.alt = img.alt || '';
    this.scale = 1;
    this.x = 0;
    this.y = 0;
    this.apply();

    this.modal.hidden = false;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => this.modal.classList.add('is-open'));
    this.closeBtn.focus();
  },

  close() {
    this.modal.classList.remove('is-open');
    this.modal.hidden = true;
    document.body.style.overflow = '';
    this.pointers.clear();
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
  },

  reset() {
    this.scale = 1;
    this.x = 0;
    this.y = 0;
    this.apply();
  },

  zoomBy(factor, cx, cy) {
    this.zoomTo(this.scale * factor, cx, cy);
  },

  // Hace zoom manteniendo fijo el punto (cx, cy) de la pantalla
  zoomTo(newScale, cx, cy) {
    newScale = Math.min(this.MAX, Math.max(this.MIN, newScale));
    const rect = this.stage.getBoundingClientRect();
    const ox = (cx === undefined ? rect.left + rect.width / 2 : cx) - (rect.left + rect.width / 2);
    const oy = (cy === undefined ? rect.top + rect.height / 2 : cy) - (rect.top + rect.height / 2);
    const ratio = newScale / this.scale;
    this.x = ox - (ox - this.x) * ratio;
    this.y = oy - (oy - this.y) * ratio;
    this.scale = newScale;
    this.clampPan();
    this.apply();
  },

  clampPan() {
    if (this.scale <= 1) {
      this.x = 0;
      this.y = 0;
      return;
    }
    const maxX = Math.max(0, (this.img.offsetWidth * this.scale - this.stage.clientWidth) / 2);
    const maxY = Math.max(0, (this.img.offsetHeight * this.scale - this.stage.clientHeight) / 2);
    this.x = Math.min(maxX, Math.max(-maxX, this.x));
    this.y = Math.min(maxY, Math.max(-maxY, this.y));
  },

  apply() {
    this.img.style.transform = 'translate(' + this.x + 'px,' + this.y + 'px) scale(' + this.scale + ')';
    this.level.textContent = Math.round(this.scale * 100) + '%';
    this.btnOut.disabled = this.scale <= this.MIN;
    this.btnIn.disabled = this.scale >= this.MAX;
    this.stage.classList.toggle('is-zoomed', this.scale > 1);
  },

  onDown(e) {
    this.downTarget = e.target;
    this.dragged = false;
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    try { this.stage.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
    if (this.pointers.size === 2) this.pinchDist = this.distance();
    this.img.style.transition = 'none';
  },

  onMove(e) {
    const p = this.pointers.get(e.pointerId);
    if (!p) return;
    const dx = e.clientX - p.x;
    const dy = e.clientY - p.y;
    p.x = e.clientX;
    p.y = e.clientY;

    if (this.pointers.size === 2) {
      const d = this.distance();
      if (this.pinchDist) {
        const pts = Array.from(this.pointers.values());
        this.zoomTo(this.scale * d / this.pinchDist, (pts[0].x + pts[1].x) / 2, (pts[0].y + pts[1].y) / 2);
      }
      this.pinchDist = d;
      this.dragged = true;
      return;
    }

    if (this.scale > 1 && (dx || dy)) {
      this.x += dx;
      this.y += dy;
      this.clampPan();
      this.apply();
      this.dragged = true;
    }
  },

  onUp(e) {
    this.pointers.delete(e.pointerId);
    this.pinchDist = 0;
    if (!this.pointers.size) this.img.style.transition = '';
    setTimeout(() => { this.dragged = false; }, 0);
  },

  distance() {
    const pts = Array.from(this.pointers.values());
    return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
  }
};

// ─── QUANTITY SELECTOR ───
const QuantitySelector = {
  init() {
    document.querySelectorAll('.quantity-selector').forEach(selector => {
      const decreaseBtn = selector.querySelector('[data-action="decrease"]');
      const increaseBtn = selector.querySelector('[data-action="increase"]');
      const input = selector.querySelector('.qty-input');

      if (!input) return;

      decreaseBtn?.addEventListener('click', () => {
        const current = parseInt(input.value) || 1;
        if (current > 1) {
          input.value = current - 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      increaseBtn?.addEventListener('click', () => {
        const current = parseInt(input.value) || 1;
        const max = parseInt(input.getAttribute('max')) || 999;
        if (current < max) {
          input.value = current + 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      input.addEventListener('change', () => {
        const val = parseInt(input.value);
        if (isNaN(val) || val < 1) input.value = 1;
      });
    });
  }
};

// ─── CART COUNT UPDATER ───
const CartCount = {
  init() {
    const countEl = document.getElementById('cart-count');
    const bodyCount = parseInt(document.body.getAttribute('data-cart-count')) || 0;
    if (countEl) {
      countEl.textContent = bodyCount > 0 ? bodyCount : '';
    }
  },

  update(count) {
    const countEl = document.getElementById('cart-count');
    if (countEl) {
      countEl.textContent = count > 0 ? count : '';
    }
  }
};

// ─── SMOOTH SCROLL ───
const SmoothScroll = {
  init() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', (e) => {
        const targetId = anchor.getAttribute('href').substring(1);
        const target = document.getElementById(targetId);
        if (target) {
          e.preventDefault();
          const headerHeight = document.getElementById('site-header')?.offsetHeight || 0;
          const announcementHeight = document.getElementById('announcement-bar')?.offsetHeight || 0;
          const offset = headerHeight + announcementHeight + 16;
          const top = target.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      });
    });
  }
};

// ─── STICKY HEADER SHADOW ───
const StickyHeader = {
  header: null,
  lastScroll: 0,

  init() {
    this.header = document.getElementById('site-header');
    if (!this.header) return;
    
    window.addEventListener('scroll', () => this.onScroll(), { passive: true });
  },

  onScroll() {
    const currentScroll = window.scrollY;
    if (currentScroll > 10) {
      this.header.style.boxShadow = '0 2px 20px rgba(0,0,0,0.12)';
    } else {
      this.header.style.boxShadow = '';
    }
    this.lastScroll = currentScroll;
  }
};

// ─── INTERSECTION OBSERVER (animate on scroll) ───
const AnimateOnScroll = {
  init() {
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

    document.querySelectorAll('.animate-on-scroll').forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(24px)';
      el.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
      observer.observe(el);
    });
  }
};

// ─── VARIANT SELECTOR ───
const VariantSelector = {
  init() {
    document.querySelectorAll('.variant-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const group = btn.closest('.variant-selector');
        group?.querySelectorAll('.variant-btn').forEach(b => b.classList.remove('selected'));
        btn.classList.add('selected');
      });
    });
  }
};

// ─── CART AJAX (optional enhancement) ───
const AjaxCart = {
  init() {
    // Hook into Shopify's cart API for dynamic updates
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('[data-cart-form]');
      if (!form) return;

      // "Comprar ahora" (name="checkout"): envío normal del formulario hacia el checkout
      if (e.submitter && e.submitter.name === 'checkout') return;

      const addToCartBtn = form.querySelector('[data-add-to-cart]');
      if (!addToCartBtn) return;

      e.preventDefault();
      
      const originalText = addToCartBtn.textContent;
      addToCartBtn.disabled = true;
      addToCartBtn.innerHTML = '<span class="spinner"></span>';

      try {
        const formData = new FormData(form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) throw new Error('Error al agregar al carrito');
        
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        CartCount.update(cart.item_count);
        
        addToCartBtn.textContent = '¡Agregado!';
        addToCartBtn.style.background = '#2d6a4f';
        
        setTimeout(() => {
          addToCartBtn.textContent = originalText;
          addToCartBtn.style.background = '';
          addToCartBtn.disabled = false;
        }, 2000);
        
      } catch (error) {
        console.error('Cart error:', error);
        addToCartBtn.textContent = 'Error, intenta de nuevo';
        setTimeout(() => {
          addToCartBtn.textContent = originalText;
          addToCartBtn.disabled = false;
        }, 2000);
      }
    });
  }
};

// ─── PRICE FORMATTER ───
const PriceFormatter = {
  format(cents) {
    const amount = cents / 100;
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }
};

// ─── WHATSAPP PULSE ───
const WhatsAppFloat = {
  init() {
    const btn = document.querySelector('.whatsapp-float');
    if (!btn) return;
    
    // Pulse after 5 seconds
    setTimeout(() => {
      btn.classList.add('animate-pulse');
      setTimeout(() => btn.classList.remove('animate-pulse'), 4000);
    }, 5000);
  }
};

// ─── INIT ALL ───
document.addEventListener('DOMContentLoaded', () => {
  AnnouncementBar.init();
  MobileMenu.init();
  SearchBar.init();
  FAQ.init();
  ProductGallery.init();
  ImageZoom.init();
  QuantitySelector.init();
  CartCount.init();
  SmoothScroll.init();
  StickyHeader.init();
  AnimateOnScroll.init();
  VariantSelector.init();
  AjaxCart.init();
  WhatsAppFloat.init();
});

// Expose utilities globally
window.ThemeUtils = {
  PriceFormatter,
  CartCount
};
